import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAppSelector } from '../../redux/store';
import api from '../../Services/api';
import Globals from '../../Globals.json';
import { notify, notifyError } from '../notify/notify-component';
import * as S from '../ModalAjusteImpressoras/styled';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface TaxProfile {
  id: number;
  tax_profile_name: string;
}

interface FlatCategory {
  id: number;
  name: string;
  level: number;
  childrenIds: number[];
}

// categoria -> perfil fiscal (uma categoria só pode ter um perfil)
type Assignment = Record<number, number | null>;

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Achata a árvore, guarda os ids das descendentes e o perfil atual de cada categoria.
function flatten(
  nodes: any[],
  level: number,
  list: FlatCategory[],
  assignment: Assignment
): number[] {
  const ids: number[] = [];
  nodes.forEach((node) => {
    const entry: FlatCategory = {
      id: node.id,
      name: node.name,
      level,
      childrenIds: [],
    };
    list.push(entry);
    ids.push(node.id);
    assignment[node.id] = node.tax_profile_id || null;

    entry.childrenIds = flatten(
      node.relationships?.children || [],
      level + 1,
      list,
      assignment
    );
    ids.push(...entry.childrenIds);
  });
  return ids;
}

export function ModalAjustePerfilFiscal({ show, onHide }: Props) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);

  const [profiles, setProfiles] = useState<TaxProfile[]>([]);
  const [categories, setCategories] = useState<FlatCategory[]>([]);
  const [original, setOriginal] = useState<Assignment>({});
  const [assignment, setAssignment] = useState<Assignment>({});
  const [activeProfile, setActiveProfile] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [applyToProducts, setApplyToProducts] = useState<boolean>(true);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [profileResponse, categoryResponse] = await Promise.all([
        api.get(
          `${Globals.api.taxProfile}?store_id=${storeMe?.storeActive?.id}`
        ),
        api.get(`${Globals.api.treeCategories}`, {
          params: {
            page: 1,
            paginate: false,
            store_id: storeMe?.storeActive?.id,
          },
        }),
      ]);

      const loadedProfiles: TaxProfile[] = profileResponse.data.data || [];
      const list: FlatCategory[] = [];
      const current: Assignment = {};
      flatten(categoryResponse.data.data || [], 0, list, current);

      // Categorias copiadas de outra loja podem apontar para um perfil que não
      // existe nesta loja: ficam sem perfil (aparecem como alteração pendente).
      const validIds = new Set(loadedProfiles.map((p) => p.id));
      const cleaned: Assignment = {};
      Object.entries(current).forEach(([id, profileId]) => {
        cleaned[Number(id)] =
          profileId && validIds.has(profileId) ? profileId : null;
      });

      setProfiles(loadedProfiles);
      setActiveProfile(loadedProfiles[0]?.id || 0);
      setCategories(list);
      setOriginal(current);
      setAssignment(cleaned);
    } catch (error) {
      notifyError('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoadingData(false);
    }
  }, [storeMe?.storeActive?.id]);

  useEffect(() => {
    if (show) {
      setSearch('');
      loadData();
    }
  }, [show, loadData]);

  const profileName = useMemo(
    () =>
      profiles.reduce((acc, p) => {
        acc[p.id] = p.tax_profile_name;
        return acc;
      }, {} as Record<number, string>),
    [profiles]
  );

  // Diferença em relação ao que já estava salvo, agrupada por perfil.
  const changes = useMemo(() => {
    const changed = categories.filter(
      (c) => (assignment[c.id] ?? null) !== (original[c.id] ?? null)
    );
    return {
      perProfile: profiles
        .map((p) => ({
          tax_profile_id: p.id,
          attach_category_ids: changed
            .filter((c) => assignment[c.id] === p.id)
            .map((c) => c.id),
        }))
        .filter((c) => c.attach_category_ids.length),
      detach_category_ids: changed
        .filter((c) => assignment[c.id] == null)
        .map((c) => c.id),
      total: changed.length,
    };
  }, [categories, profiles, assignment, original]);

  const countByProfile = useMemo(() => {
    const counts: Record<number, number> = {};
    Object.values(assignment).forEach((id) => {
      if (id) counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [assignment]);

  const changedProfiles = useMemo(
    () => new Set(changes.perProfile.map((c) => c.tax_profile_id)),
    [changes]
  );

  const visibleCategories = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return categories;
    return categories.filter((c) => normalize(c.name).includes(term));
  }, [categories, search]);

  // Marcar uma categoria a vincula ao perfil ativo (tirando de outro perfil, se houver);
  // desmarcar remove o vínculo. A mudança vale também para as subcategorias.
  const applyTo = (ids: number[], checked: boolean) =>
    setAssignment((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = checked ? null : activeProfile;
      });
      return next;
    });

  const handleToggle = (category: FlatCategory) =>
    applyTo(
      [category.id, ...category.childrenIds],
      assignment[category.id] === activeProfile
    );

  const allVisibleChecked =
    visibleCategories.length > 0 &&
    visibleCategories.every((c) => assignment[c.id] === activeProfile);

  const handleToggleVisible = () =>
    applyTo(
      visibleCategories.map((c) => c.id),
      allVisibleChecked
    );

  const handleSave = async () => {
    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    // Envia o estado completo (não só a diferença): assim o salvar também
    // corrige categorias/produtos copiados de outra loja ou dessincronizados.
    // O backend só grava o que realmente estiver diferente.
    const perProfile = profiles
      .map((p) => ({
        tax_profile_id: p.id,
        attach_category_ids: categories
          .filter((c) => assignment[c.id] === p.id)
          .map((c) => c.id),
      }))
      .filter((c) => c.attach_category_ids.length);
    const detachIds = categories
      .filter((c) => assignment[c.id] == null)
      .map((c) => c.id);

    setSaving(true);
    try {
      await api.post(Globals.api.categoriesBulkTaxProfile, {
        store_id: storeMe.storeActive.id,
        apply_to_products: applyToProducts,
        ...(perProfile.length ? { changes: perProfile } : {}),
        ...(detachIds.length ? { detach_category_ids: detachIds } : {}),
      });
      notify('Perfis fiscais das categorias atualizados com sucesso!');
      onHide();
    } catch (error) {
      notifyError('Erro ao atualizar perfis fiscais das categorias');
      console.error('Erro ao atualizar perfis fiscais:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.ModalWrapper show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ajuste de Perfil Fiscal por Categoria</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loadingData ? (
          <div className="text-center p-4">
            <div className="spinner-border" role="status" />
          </div>
        ) : profiles.length === 0 ? (
          <S.Muted>Nenhum perfil fiscal cadastrado.</S.Muted>
        ) : (
          <S.Layout>
            <S.PrinterList>
              {profiles.map((p) => (
                <S.PrinterItem
                  key={p.id}
                  type="button"
                  $active={p.id === activeProfile}
                  onClick={() => setActiveProfile(p.id)}
                >
                  <span>{p.tax_profile_name}</span>
                  <S.Badge $changed={changedProfiles.has(p.id)}>
                    {countByProfile[p.id] || 0}
                  </S.Badge>
                </S.PrinterItem>
              ))}
            </S.PrinterList>

            <S.Panel>
              <S.Toolbar>
                <Form.Control
                  size="sm"
                  placeholder="Buscar categoria..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleToggleVisible}
                  disabled={visibleCategories.length === 0}
                >
                  {allVisibleChecked ? 'Desmarcar todas' : 'Marcar todas'}
                </Button>
              </S.Toolbar>

              <S.Muted className="mb-2">
                Categorias com o perfil <b>{profileName[activeProfile]}</b>.
                Cada categoria tem um único perfil: marcar aqui a remove do
                perfil anterior.
              </S.Muted>

              <S.Tree>
                {visibleCategories.length === 0 ? (
                  <S.Muted className="d-block p-3">
                    Nenhuma categoria encontrada.
                  </S.Muted>
                ) : (
                  visibleCategories.map((category) => {
                    const assigned = assignment[category.id];
                    const checked = assigned === activeProfile;
                    return (
                      <S.Row
                        key={category.id}
                        $checked={checked}
                        $level={search ? 0 : category.level}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(category)}
                        />
                        {category.name.toUpperCase()}
                        {assigned && !checked && (
                          <S.Muted className="ms-auto">
                            {profileName[assigned]}
                          </S.Muted>
                        )}
                      </S.Row>
                    );
                  })
                )}
              </S.Tree>
            </S.Panel>
          </S.Layout>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div>
          <Form.Check
            type="switch"
            id="apply-tax-profile-to-products"
            label="Refletir também nos produtos das categorias"
            checked={applyToProducts}
            onChange={(e) => setApplyToProducts(e.target.checked)}
          />
          <S.Muted>
            {changes.total === 0
              ? 'Nenhuma alteração pendente — salvar reaplica a configuração atual'
              : `${changes.total} categoria(s) com alterações pendentes`}
          </S.Muted>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || loadingData || categories.length === 0}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </Modal.Footer>
    </S.ModalWrapper>
  );
}
